import type { FeatureFlagName } from '@/libs/feature-flags/flags';
import { Env } from '@/consts/Env';
import { FeatureFlag } from '@/libs/feature-flags/flags';

type FeatureFlagOverrides = Partial<Record<FeatureFlagName, boolean>>;

const FLAG_NAMES = new Set<string>(Object.values(FeatureFlag));

const resolveFlagName = (key: string): FeatureFlagName | undefined => {
  if (key in FeatureFlag) {
    return FeatureFlag[key as keyof typeof FeatureFlag];
  }

  return FLAG_NAMES.has(key) ? key as FeatureFlagName : undefined;
};

/**
 * Parses a JSON object such as `{"GAMIFICATION_ENABLED":true}`. Keys accept
 * either the `FeatureFlag` constant name or the Unleash flag name.
 */
export function parseFeatureFlagOverrides(raw: string | undefined): FeatureFlagOverrides {
  const overrides: FeatureFlagOverrides = {};

  if (!raw?.trim()) {
    return overrides;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn('[feature-flags] Local overrides must be a JSON object');
    return overrides;
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.warn('[feature-flags] Local overrides must be a JSON object');
    return overrides;
  }

  for (const [key, value] of Object.entries(parsed)) {
    const flag = resolveFlagName(key);

    if (!flag || typeof value !== 'boolean') {
      console.warn(`[feature-flags] Ignoring invalid local override "${key}"`);
      continue;
    }

    overrides[flag] = value;
  }

  return overrides;
}

// Local overrides only exist in `next dev`; production builds always use Unleash.
const localOverrides: FeatureFlagOverrides = Env.NODE_ENV === 'development'
  ? parseFeatureFlagOverrides(Env.NEXT_PUBLIC_LOCAL_FEATURE_FLAG_OVERRIDES)
  : {};

export function getFeatureFlagOverride(flag: FeatureFlagName): boolean | undefined {
  return localOverrides[flag];
}
